import { Controller } from '@hotwired/stimulus'
import { createApp } from 'vue'
import consumer from '~/channels/consumer'

/**
 * Block Editor Controller
 *
 * Handles the tabbed block editor sidebar with:
 * - Content tab: Basic text editing (heading, subheading, body)
 * - Style tab: Surface and visual settings
 * - Background tab: Image/video backgrounds (Vue component)
 */
export default class extends Controller {
  static targets = [
    'tabButton',
    'contentTab',
    'styleTab',
    'backgroundTab',
    'contentPanel',
    'stylePanel',
    'backgroundPanel',
    'backgroundMount'
  ]

  static values = {
    websiteId: String,
    pageId: String,
    blockId: String,
    blockType: String,
    blockJson: String, // Base64-encoded JSON to avoid parsing issues
    csrf: String
  }

  connect() {
    this.vueApp = null
    this.backgroundSubscription = null
    this.currentTab = 'content'
    this.blockData = null

    // Decode block data from base64
    if (this.blockJsonValue) {
      try {
        this.blockData = JSON.parse(decodeURIComponent(atob(this.blockJsonValue)))
      } catch (e) {
        console.error('Failed to parse block data:', e)
        this.blockData = {}
      }
    }

    // Setup ActionCable for background updates
    if (this.blockIdValue) {
      this.setupBackgroundChannel()
    }
  }

  disconnect() {
    this.unmountVue()
    if (this.backgroundSubscription) {
      this.backgroundSubscription.unsubscribe()
    }
  }

  // ==========================================
  // Tab Navigation
  // ==========================================

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    if (tab === this.currentTab) return

    this.currentTab = tab

    // Update tab button styles
    this.tabButtonTargets.forEach((btn) => {
      const isActive = btn.dataset.tab === tab
      btn.classList.toggle('border-blue-500', isActive)
      btn.classList.toggle('text-blue-600', isActive)
      btn.classList.toggle('bg-blue-50', isActive)
      btn.classList.toggle('border-transparent', !isActive)
      btn.classList.toggle('text-gray-500', !isActive)
      btn.classList.toggle('hover:text-gray-700', !isActive)
      btn.classList.toggle('hover:bg-gray-50', !isActive)
    })

    // Show/hide panels
    if (this.hasContentPanelTarget) {
      this.contentPanelTarget.classList.toggle('hidden', tab !== 'content')
    }
    if (this.hasStylePanelTarget) {
      this.stylePanelTarget.classList.toggle('hidden', tab !== 'style')
    }
    if (this.hasBackgroundPanelTarget) {
      this.backgroundPanelTarget.classList.toggle('hidden', tab !== 'background')
    }

    // Mount/unmount Vue for background tab
    if (tab === 'background') {
      this.mountBackgroundEditor()
    } else {
      this.unmountVue()
    }
  }

  // ==========================================
  // Background Editor (Vue)
  // ==========================================

  async mountBackgroundEditor() {
    if (this.vueApp || !this.hasBackgroundMountTarget) return

    // Dynamic import of the Vue component
    const { default: BlockBackgroundEditor } = await import('~/javascript/components/admin/page_editor/BlockBackgroundEditor.vue')

    const props = {
      blockId: this.blockIdValue,
      websiteId: this.websiteIdValue,
      pageId: this.pageIdValue,
      initialSettings: this.blockData?.settings?.background || {},
      // Pass callback for when settings are updated
      onUpdate: (settings) => this.handleBackgroundUpdate(settings)
    }

    this.vueApp = createApp(BlockBackgroundEditor, props)
    this.vueApp.mount(this.backgroundMountTarget)
  }

  unmountVue() {
    if (this.vueApp) {
      this.vueApp.unmount()
      this.vueApp = null
    }
  }

  handleBackgroundUpdate(backgroundSettings) {
    // Extract rendered_html before updating block data (it's not part of background settings)
    const renderedHtml = backgroundSettings.rendered_html
    delete backgroundSettings.rendered_html

    // Update local block data
    if (this.blockData) {
      const settings = this.blockData.settings || {}
      settings.background = backgroundSettings
      this.blockData = { ...this.blockData, settings }
    }

    // Dispatch event for page editor to sync
    this.dispatch('backgroundUpdated', {
      detail: {
        blockId: this.blockIdValue,
        background: backgroundSettings,
        renderedHtml: renderedHtml
      }
    })
  }

  // ==========================================
  // ActionCable for Background Generation
  // ==========================================

  setupBackgroundChannel() {
    this.backgroundSubscription = consumer.subscriptions.create(
      { channel: 'BlockBackgroundChannel', block_id: this.blockIdValue },
      {
        received: (data) => this.handleBackgroundMessage(data)
      }
    )
  }

  handleBackgroundMessage(data) {
    switch (data.event) {
      case 'background_generation_started':
        // Vue component handles its own loading state
        break
      case 'background_generation_completed':
        // Update local block data with new background settings before remounting
        if (this.blockData && data.background) {
          const settings = this.blockData.settings || {}
          settings.background = data.background
          this.blockData = { ...this.blockData, settings }
        }
        // Refresh the Vue component with new data
        if (this.vueApp && this.currentTab === 'background') {
          this.unmountVue()
          this.mountBackgroundEditor()
        }
        // Dispatch event for page editor to sync
        this.dispatch('backgroundUpdated', {
          detail: {
            blockId: this.blockIdValue,
            background: data.background
          }
        })
        break
      case 'background_generation_failed':
        // Vue component handles error display
        break
    }
  }

  // ==========================================
  // Content Form Handling
  // ==========================================

  async saveContent(event) {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)

    const content = {
      heading: formData.get('heading'),
      subheading: formData.get('subheading'),
      body_text: formData.get('body_text')
    }

    const response = await fetch(
      `/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks/${this.blockIdValue}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify({
          block: {
            section_title: formData.get('section_title'),
            content: content
          }
        })
      }
    )

    if (response.ok) {
      const data = await response.json()
      this.dispatch('blockUpdated', { detail: data.block })
      this.showNotification('Block updated', 'success')
    } else {
      this.showNotification('Failed to update block', 'error')
    }
  }

  // ==========================================
  // Style Form Handling
  // ==========================================

  async saveSurface(event) {
    const surface = event.currentTarget.value
    const settings = this.blockData?.settings || {}
    settings.surface = surface

    const response = await fetch(
      `/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks/${this.blockIdValue}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify({
          block: { settings }
        })
      }
    )

    if (response.ok) {
      const data = await response.json()
      this.blockData = data.block
      this.dispatch('blockUpdated', { detail: data.block })
      this.showNotification('Surface updated', 'success')
    }
  }

  // ==========================================
  // Utilities
  // ==========================================

  showNotification(message, type = 'success') {
    const notification = document.createElement('div')
    notification.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg text-white ${
      type === 'success' ? 'bg-green-600' : 'bg-red-600'
    } shadow-lg z-50 transition-opacity duration-300`
    notification.textContent = message
    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 2000)
  }
}
